import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

export interface SearchProgress {
  searchId: string;
  collected: number;
  target: number;
  processed: number;
  status: 'started' | 'processing' | 'completed' | 'error' | 'cancelled' | 'hard_stop';
  percentage: number;
  newlyScored?: number;
}

export const useSearchProgress = () => {
  const [progress, setProgress] = useState<SearchProgress | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const currentSearchIdRef = useRef<string | null>(null);

  // Get WebSocket URL and path based on REACT_APP_RELEVANTER_BASE_URL
  const getSocketConfig = useCallback(() => {
    const apiBaseUrl = process.env.REACT_APP_RELEVANTER_BASE_URL || '/relevanter';

    // If it's a relative path (e.g. '/relevanter' - production with nginx proxy)
    if (apiBaseUrl.startsWith('/')) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Use the path from env variable + /api/socket.io
      const basePath = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
      return {
        url: `${protocol}//${window.location.host}`,
        path: `${basePath}/api/socket.io`
      };
    }

    // If it's a full URL (e.g. 'http://localhost:3001/api' - local; 'https://hr-dev.acm-ai.ru/relevanter' - dev/qa/prod)
    try {
      const url = new URL(apiBaseUrl);
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      const basePath = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
      const isLocalhost = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
      // Localhost — прямое подключение к backend, у него socket.io на `/socket.io`.
      // Если REACT_APP_RELEVANTER_BASE_URL=...localhost:3001/api, basePath=/api — игнорируем,
      // иначе WS падает с 404 (бэк не слушает /api/socket.io).
      // Dev/qa/prod — за nginx-проксей, нужен basePath (например /relevanter/socket.io),
      // чтобы прокся переадресовала на backend.
      return {
        url: `${protocol}//${url.host}`,
        path: isLocalhost ? '/socket.io' : `${basePath}/socket.io`
      };
    } catch {
      return {
        url: window.location.origin,
        path: '/relevanter/api/socket.io'
      };
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (socketRef.current?.connected) return;

    const config = getSocketConfig();
    console.log('[SearchProgress] Connecting to WebSocket:', config.url, 'path:', config.path);

    socketRef.current = io(config.url, {
      path: config.path,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect', () => {
      console.log('[SearchProgress] WebSocket connected');
      setIsConnected(true);

      // Re-join search room if we have an active search
      if (currentSearchIdRef.current) {
        socketRef.current?.emit('join-search', currentSearchIdRef.current);
      }
    });

    socketRef.current.on('disconnect', () => {
      console.log('[SearchProgress] WebSocket disconnected');
      setIsConnected(false);
    });

    socketRef.current.on('connect_error', (error) => {
      console.warn('[SearchProgress] WebSocket connection error:', error.message);
    });

    socketRef.current.on('search-progress', (data: SearchProgress) => {
      console.log('[SearchProgress] Received progress:', data);
      if (data.searchId === currentSearchIdRef.current) {
        setProgress(data);
      }
    });
  }, [getSocketConfig]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Start tracking a search
  const startTracking = useCallback((searchId: string) => {
    console.log('[SearchProgress] Starting tracking for search:', searchId);
    currentSearchIdRef.current = searchId;
    setProgress(null);

    // Connect if not connected
    if (!socketRef.current?.connected) {
      connect();
      // Wait for connection before joining room
      // The connect handler will join the room using currentSearchIdRef
    } else {
      // Already connected, join the search room immediately
      socketRef.current?.emit('join-search', searchId);
    }
  }, [connect]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    console.log('[SearchProgress] Stopping tracking');
    currentSearchIdRef.current = null;
    setProgress(null);
  }, []);

  // Generate unique search ID
  const generateSearchId = useCallback(() => {
    return `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    progress,
    isConnected,
    connect,
    disconnect,
    startTracking,
    stopTracking,
    generateSearchId,
  };
};
